import React from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

/* ─── Data ───────────────────────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 2L13.5 7.5H19L14.5 11L16.5 17L11 13.5L5.5 17L7.5 11L3 7.5H8.5L11 2Z"
          stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Venture-Type Precision',
    // MLA: Sugarman principle — specificity over vagueness; match the reader's context
    body: 'Startup. Franchise. Real estate play. Product launch. MarginPro reads the room — calibrating every ratio and benchmark to your exact type of venture, not a generic template.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <rect x="2" y="14" width="4" height="6" rx="1" stroke="currentColor" strokeWidth="1.6"/>
        <rect x="9" y="9" width="4" height="11" rx="1" stroke="currentColor" strokeWidth="1.6"/>
        <rect x="16" y="4" width="4" height="16" rx="1" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
    title: 'The Ratios That Actually Matter',
    // MLA: Sugarman — lead with the feeling (confidence), follow with the facts (specific ratios)
    body: 'Gross margin. Break-even point. ROI. Burn rate. Overhead ratio. The five numbers that separate a viable venture from an expensive lesson — surfaced before you write a single check.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M3 11h16M3 6h10M3 16h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Questions That Go Somewhere',
    // MLA: Sugarman — the slippery slide; every question pulls to the next insight
    body: 'One answer shapes the next question. MarginPro routes you down the path most relevant to your venture — no dead ends, no irrelevant detours, no wasted time.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M11 7v4l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Decisions, Not Reports',
    // MLA: Sugarman — sell the concept; the concept here is clarity, not data
    body: 'Every output is a prioritized checklist. You leave knowing your next move — not needing to interpret a fifteen-page document to find it.',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Define your venture',
    // MLA: Sugarman Ch. 7 — resonate with the reader's existing mental model
    body: 'Select the type of project you\'re evaluating and set the context. MarginPro calibrates its entire analysis — ratios, benchmarks, risk flags — from this single starting point.',
  },
  {
    step: '02',
    title: 'Answer the right questions',
    // MLA: Sugarman Ch. 8 — slippery slide; no friction, no stops
    body: 'A branching questionnaire surfaces only what\'s relevant to your entity structure, industry, and business stage. Skip anything you\'re unsure about. Keep moving.',
  },
  {
    step: '03',
    title: 'Receive your financial picture',
    // MLA: Sugarman Ch. 11 — emotion leads, logic follows
    body: 'A margin score. Essential ratios with benchmarks. A prioritized checklist of next-step decisions. Everything you need to move forward with confidence — built entirely from your inputs.',
  },
];

const PRICING = [
  {
    tier: 'Starter',
    price: '0',
    period: '/month',
    tagline: 'Run the numbers before you commit.',
    features: [
      '3 questionnaire sessions/month',
      'Core financial ratios',
      'Decision checklist output',
      'Single venture type',
    ],
    cta: 'Start Free',
    ctaHref: '/chat',
    highlight: false,
  },
  {
    tier: 'Pro',
    price: '29',
    period: '/month',
    tagline: 'For operators who move fast and think faster.',
    features: [
      'Unlimited sessions',
      'All venture types',
      'Full metrics suite',
      'Session history',
      'Priority guidance tips',
    ],
    cta: 'Get Pro',
    ctaHref: '/chat',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    tier: 'VIP',
    price: '79',
    period: '/month',
    tagline: 'For advisors managing multiple plays at once.',
    features: [
      'Everything in Pro',
      'Multi-venture comparison',
      'AI-powered recommendations',
      'White-label exports',
      'Dedicated support',
    ],
    cta: 'Go VIP',
    ctaHref: '/chat',
    highlight: false,
  },
];

/* ─── Sections ───────────────────────────────────────────────────────────── */
const HeroSection = () => (
  <section className="hero" id="hero">
    <div className="hero__glow" aria-hidden="true" />
    <div className="hero__glow hero__glow--2" aria-hidden="true" />

    <div className="container hero__content">
      {/* Eyebrow */}
      <div className="hero__eyebrow">
        <span className="hero__eyebrow-dot" />
        <span>Business Finance Intelligence</span>
      </div>

      {/* Headline — Adweek: stops the right reader with a direct, specific promise */}
      <h1 className="hero__headline">
        Know your numbers<br />
        <span className="gold-text">before you move.</span>
      </h1>

      {/* Sub — Adweek: first sentence that compels; names the pain and promises the cure */}
      <p className="hero__sub">
        Most ventures fail on assumptions, not ideas.
        MarginPro runs the financial analysis that separates confident operators
        from costly mistakes — before a single dollar is committed.
      </p>

      <div className="hero__cta-row">
        <Link to="/chat" className="btn btn--gold btn--lg">
          Go to Chat
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{marginLeft: 8}}>
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
        <a href="#how-it-works" className="btn btn--ghost btn--lg">See how it works</a>
      </div>

      <div className="hero__app-strip glass-card">
        <div className="hero__app-strip-label">Coming soon to mobile</div>
        <div className="hero__app-store-btns">
          <div className="hero__app-btn" title="Download on the App Store (coming soon)">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13.3 1.7A4.1 4.1 0 0116 5.3 4 4 0 0113.5 9a4 4 0 01-2.5-3.8 4 4 0 012.3-3.5z" fill="currentColor"/>
              <path d="M16.7 14.5c-.4.9-.9 1.7-1.6 2.4-.9.9-1.8 1.3-2.7 1.3-.7 0-1.6-.4-2.7-1.1-1.1.7-2 1.1-2.7 1.1-1 0-2-.5-2.9-1.5C3 15.6 2 13.6 2 11.4c0-2 .5-3.6 1.6-4.8.9-1 2-1.5 3.3-1.5.7 0 1.6.3 2.7.9 1-.6 1.9-.9 2.7-.9.5 0 1 .1 1.5.3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <div>
              <span className="hero__app-btn-label">Download on the</span>
              <span className="hero__app-btn-store">App Store</span>
            </div>
          </div>
          <div className="hero__app-btn" title="Get it on Google Play (coming soon)">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 2.5L16 10 4 17.5V2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
            <div>
              <span className="hero__app-btn-label">Get it on</span>
              <span className="hero__app-btn-store">Google Play</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeaturesSection = () => (
  <section className="section" id="features">
    <div className="container">
      <div className="section__header">
        {/* Adweek eyebrow: seeds curiosity, addresses a specific type of reader */}
        <p className="section__eyebrow">Built for decision-makers</p>
        <h2 className="section__title">
          Financial clarity,<br /><span className="gold-text">every step of the way.</span>
        </h2>
        {/* MLA: Sugarman Ch. 7 — resonate with the reader; mirror their frustration with generic tools */}
        <p className="section__sub">
          MarginPro isn't a spreadsheet you have to populate, a consultant you have to brief,
          or a report you have to decode. It's a structured sounding board that gives you
          the ratios you need — calibrated to your specific venture, every time.
        </p>
      </div>
      <div className="features__grid">
        {FEATURES.map((f) => (
          <div key={f.title} className="feature-card glass-card">
            <div className="feature-card__icon">{f.icon}</div>
            <h3 className="feature-card__title">{f.title}</h3>
            <p className="feature-card__body">{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="section section--alt" id="how-it-works">
    <div className="container">
      <div className="section__header">
        <p className="section__eyebrow">The process</p>
        {/* Adweek: headline promises a specific, measurable outcome */}
        <h2 className="section__title">Three steps to<br /><span className="gold-text">a clear decision.</span></h2>
        {/* MLA: Sugarman Ch. 14 — write as much as the sale requires; this is short because trust is already building */}
        <p className="section__sub">
          Under ten minutes from first question to actionable output.
          No spreadsheets. No consultants. No guessing.
        </p>
      </div>
      <div className="how-grid">
        {HOW_IT_WORKS.map((h) => (
          <div key={h.step} className="how-item">
            <div className="how-item__step">{h.step}</div>
            <h3 className="how-item__title">{h.title}</h3>
            <p className="how-item__body">{h.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PricingSection = () => (
  <section className="section" id="pricing">
    <div className="container">
      <div className="section__header">
        <p className="section__eyebrow">Pricing</p>
        {/* Adweek: headline names a universal truth about the buyer's journey */}
        <h2 className="section__title">One plan for every<br /><span className="gold-text">stage of growth.</span></h2>
        {/* MLA: Sugarman Ch. 19 — greed trigger: more value than they expect to pay */}
        <p className="section__sub">Start free. Scale when the volume demands it.</p>
      </div>
      <div className="pricing__grid">
        {PRICING.map((plan) => (
          <div
            key={plan.tier}
            className={`pricing-card glass-card${plan.highlight ? ' pricing-card--highlight' : ''}`}
          >
            {plan.badge && <div className="pricing-card__badge">{plan.badge}</div>}
            <div className="pricing-card__tier">{plan.tier}</div>
            <div className="pricing-card__price">
              <span className="pricing-card__currency">$</span>
              <span className="pricing-card__amount">{plan.price}</span>
              <span className="pricing-card__period">{plan.period}</span>
            </div>
            <p className="pricing-card__tagline">{plan.tagline}</p>
            <ul className="pricing-card__features">
              {plan.features.map((feat) => (
                <li key={feat} className="pricing-card__feature">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="pricing-card__check">
                    <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feat}
                </li>
              ))}
            </ul>
            <Link
              to={plan.ctaHref}
              className={`btn btn--lg pricing-card__cta${plan.highlight ? ' btn--gold' : ' btn--outline'}`}
            >
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CtaSection = () => (
  <section className="section cta-section">
    <div className="container">
      <div className="cta-block glass-card">
        <div className="cta-block__glow" aria-hidden="true" />
        {/* Adweek: two-line command headline — active verbs, zero filler */}
        <h2 className="cta-block__title">
          Stop guessing.<br /><span className="gold-text">Start knowing.</span>
        </h2>
        {/* MLA: Sugarman Ch. 19 — instant gratification trigger; remove time and complexity objections */}
        <p className="cta-block__sub">
          Run your first venture analysis in under ten minutes.
          Free to start. No spreadsheets, no consultants, no commitment.
        </p>
        <Link to="/chat" className="btn btn--gold btn--lg">Go to Chat</Link>
      </div>
    </div>
  </section>
);

/* ─── Page ───────────────────────────────────────────────────────────────── */
const Landing = () => (
  <main>
    <HeroSection />
    <FeaturesSection />
    <HowItWorksSection />
    <PricingSection />
    <CtaSection />
  </main>
);

export default Landing;
